import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, X, Check } from "lucide-react";

/**
 * Modern city/country autocomplete powered by Open-Meteo Geocoding API.
 * - Free, no API key, no rate limits.
 * - Returns city, region, country and timezone in a single call.
 *
 * Props:
 *   value: { name, country, lat, lng, tz, display } | null
 *   onChange: (value) => void
 *   testid: string
 */
export default function CityAutocomplete({ value, onChange, testid = "city-autocomplete" }) {
  const [query, setQuery] = useState(value?.display || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync query when value changes externally (e.g. reset)
  useEffect(() => {
    setQuery(value?.display || "");
  }, [value]);

  // Click outside to close
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setResults([]); setLoading(false); return;
    }
    if (value?.display === query) return; // no refetch when reflecting selected value
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const searchCities = async (name) => {
          const r = await fetch(
            `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=100&language=en&format=json`
          );
          const data = await r.json();
          return data?.results || [];
        };

        let searchedName = query.trim();
        let raw = await searchCities(searchedName);

        // Tolerant fallback: "Sunbury Australia" / "Sunbury, VIC" → search the
        // city part, then filter by the remaining words (country / region).
        if (!raw.length) {
          const parts = query.trim().split(/[,\s]+/).filter(Boolean);
          for (let n = parts.length - 1; n >= 1 && !raw.length; n--) {
            const name = parts.slice(0, n).join(" ");
            if (name.length < 2) break;
            const rest = parts.slice(n).map((w) => w.toLowerCase());
            const candidates = await searchCities(name);
            const filtered = candidates.filter((c) =>
              rest.every((w) =>
                [c.country, c.admin1, c.admin2, c.country_code]
                  .filter(Boolean)
                  .some((f) => String(f).toLowerCase().includes(w))
              )
            );
            if (filtered.length) { raw = filtered; searchedName = name; }
          }
        }

        // Relevance sort: exact name match first, then name prefix, then by population.
        const q = searchedName.toLowerCase();
        const score = (c) => {
          const n = (c.name || "").toLowerCase();
          let s = c.population || 0;
          if (n === q) s += 1e9;
          else if (n.startsWith(q)) s += 1e8;
          return s;
        };

        const list = raw
          .slice()
          .sort((a, b) => score(b) - score(a))
          .map((c) => ({
            name: c.name,
            country: c.country,
            country_code: c.country_code,
            admin: c.admin1 || c.admin2 || "",
            lat: c.latitude,
            lng: c.longitude,
            tz: c.timezone,
            population: c.population || 0,
            display: [c.name, c.admin1, c.country].filter(Boolean).join(", "),
          }));
        setResults(list);
        setActiveIndex(list.length ? 0 : -1);
        setOpen(true);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, value]);

  const select = (city) => {
    onChange(city);
    setQuery(city.display);
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) select(results[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      <div className="relative">
        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]/70 pointer-events-none" />
        <input
          data-testid={`${testid}-input`}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); if (value) onChange(null); }}
          onFocus={() => { if (results.length && !(value && value.display === query)) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder="Start typing your city..."
          autoComplete="off"
          className="cosmic-input rounded-xl pl-10 pr-10 py-3 w-full font-sans text-base"
        />
        {loading ? (
          <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37] animate-spin" />
        ) : query ? (
          <button
            type="button"
            data-testid={`${testid}-clear`}
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-white/5 transition-colors"
            aria-label="Clear"
          >
            <X className="w-3.5 h-3.5 text-slate-400" />
          </button>
        ) : null}
      </div>

      {/* Dropdown */}
      {open && (results.length > 0 || (!loading && query.length >= 2)) && (
        <div
          data-testid={`${testid}-dropdown`}
          className="absolute z-50 mt-2 left-0 right-0 rounded-xl border border-[#D4AF37]/20 bg-[#0B0E14]/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] max-h-72 overflow-y-auto"
        >
          {results.length === 0 && !loading ? (
            <p className="px-4 py-3 text-sm text-slate-400 font-sans">
              No matches for "<span className="text-white">{query}</span>".
            </p>
          ) : (
            results.map((c, i) => (
              <button
                key={`${c.name}-${c.lat}-${c.lng}-${i}`}
                type="button"
                data-testid={`${testid}-option-${i}`}
                onClick={() => select(c)}
                onMouseEnter={() => setActiveIndex(i)}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                  activeIndex === i ? "bg-[#D4AF37]/10" : "hover:bg-white/5"
                }`}
              >
                <span className="text-lg leading-none">{flagEmoji(c.country_code)}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm text-white truncate">
                    {c.name}
                    {c.admin && <span className="text-slate-400"> · {c.admin}</span>}
                  </p>
                  <p className="font-sans text-[11px] text-slate-500">
                    {c.country} <span className="text-slate-600">·</span> {c.tz}
                  </p>
                </div>
                {value?.lat === c.lat && value?.lng === c.lng && (
                  <Check className="w-4 h-4 text-[#D4AF37]" />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Country code -> flag emoji
function flagEmoji(code) {
  if (!code || code.length !== 2) return "🌍";
  const A = 0x1F1E6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => A + c.charCodeAt(0) - a));
}
